import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowLeftRight, RotateCcw, Plus, X } from 'lucide-react';
import EmployeeSelect from '../../components/pickers/EmployeeSelect';

function daysSince(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

export default function ITAllocations() {
  const [rows, setRows] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(() => {
    apiGet('/it/assets', { status: 'allocated' }).then(async (r) => {
      // build allocation rows from active allocations on each asset
      const out = [];
      (r.items || []).forEach((a) => {
        (a.allocated_to || []).forEach((al) => out.push({
          asset_id: a.id, asset_tag: a.asset_tag, asset_name: a.name, category: a.category,
          employee_name: al.employee_name, expected_return_date: al.expected_return_date,
          allocated_at: al.allocated_at, allocation_id: al.allocation_id,
        }));
      });
      setOut(out);
    }).catch(() => toast.error('Failed to load (need IT permission)'));
  }, []);
  const setOut = (o) => setRows(o);
  useEffect(() => { load(); }, [load]);

  const doReturn = async (row) => {
    if (!window.confirm(`Return ${row.asset_name} from ${row.employee_name}?`)) return;
    try { const r = await apiPost(`/it/assets/${row.asset_id}/return`, { return_condition: 'good' });
      if (r.missing_flagged) toast.warning(`Returned — missing: ${r.accessories_missing.join(', ')}`);
      else toast.success('Returned'); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Return failed'); }
  };

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="it-allocations">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Information Technology</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Allocations</h1>
          <p className="text-sm text-slate-600 mt-1">Active device allocations across the company.</p>
        </div>
        <button onClick={() => setShowBulk(true)} data-testid="bulk-allocate-btn" className="px-4 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2"><Plus size={14} /> Bulk Allocate (Onboarding)</button>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="allocations-empty"><ArrowLeftRight size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">No active allocations.</p></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {['Employee', 'Asset', 'Category', 'Expected Return', 'Days Held', 'Actions'].map((h) => <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => {
                const overdue = row.expected_return_date && row.expected_return_date < today;
                return (
                  <tr key={row.allocation_id} data-testid={`alloc-row-${row.allocation_id}`} className={overdue ? 'bg-red-50/40' : ''}>
                    <td className="px-3 py-2 font-display font-bold">{row.employee_name}</td>
                    <td className="px-3 py-2"><span className="font-mono text-[10px] text-slate-400">{row.asset_tag}</span> {row.asset_name}</td>
                    <td className="px-3 py-2 text-slate-600">{row.category}</td>
                    <td className={`px-3 py-2 ${overdue ? 'text-red-600 font-bold' : 'text-slate-500'}`}>{row.expected_return_date || '—'}{overdue ? ' (overdue)' : ''}</td>
                    <td className="px-3 py-2">{daysSince(row.allocated_at)}</td>
                    <td className="px-3 py-2"><button onClick={() => doReturn(row)} data-testid={`alloc-return-${row.allocation_id}`} className="text-emerald-700 font-bold flex items-center gap-1"><RotateCcw size={12} /> Return</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showBulk && <BulkAllocateModal onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); load(); }} />}
    </div>
  );
}

function BulkAllocateModal({ onClose, onDone }) {
  const [emp, setEmp] = useState(null);
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiGet('/it/assets', { status: 'available' }).then((r) => setAvailable(r.items || [])).catch(() => {}); }, []);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const submit = async () => {
    if (!emp) { toast.error('Select an employee'); return; }
    if (selected.length === 0) { toast.error('Select at least one asset'); return; }
    setBusy(true);
    try {
      const r = await apiPost('/it/assets/bulk-allocate', { employee_id: emp.id, asset_ids: selected });
      toast.success(`${r.allocated} allocated${r.failed ? `, ${r.failed} failed` : ''}`); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Bulk allocate failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="bulk-allocate-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-md p-5 space-y-3 max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between"><h3 className="font-display font-bold text-sm">Bulk Allocate — New Joiner Setup</h3><button onClick={onClose}><X size={18} /></button></div>
        <div><span className="lbl">Employee *</span><EmployeeSelect value={emp} onSelect={setEmp} testid="bulk-emp" /></div>
        <div><span className="lbl">Select devices ({selected.length} selected)</span>
          <div className="border border-slate-200 rounded-sm max-h-60 overflow-y-auto divide-y divide-slate-100">
            {available.length === 0 ? <div className="p-3 text-xs text-slate-400">No available assets.</div> : available.map((a) => (
              <label key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`bulk-asset-${a.id}`}>
                <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggle(a.id)} className="accent-[#1F7A6E]" />
                <span className="font-mono text-[10px] text-slate-400">{a.asset_tag}</span>
                <span className="font-display font-bold">{a.name}</span>
                <span className="text-slate-400 text-xs">· {a.category}</span>
              </label>
            ))}
          </div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="bulk-submit" className="w-full py-2.5 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50">{busy ? 'Allocating…' : `Allocate ${selected.length} device(s)`}</button>
        <style>{`.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
      </div>
    </div>
  );
}
